/**
 * Utility functions for color manipulation and contrast calculation
 */

/**
 * Calculates the luminance (brightness) of a hex color
 * Uses the relative luminance formula from WCAG
 * @param hexColor - Color in hex format (e.g., #FFFF00)
 * @returns Luminance value between 0 (dark) and 1 (light)
 */
export function getColorLuminance(hexColor: string): number {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Apply gamma correction
  const getRgb = (c: number) => {
    if (c <= 0.03928) {
      return c / 12.92;
    }
    return Math.pow((c + 0.055) / 1.055, 2.4);
  };
  
  // Calculate relative luminance
  const luminance = 0.2126 * getRgb(r) + 0.7152 * getRgb(g) + 0.0722 * getRgb(b);
  return luminance;
}

/**
 * Determines whether to use dark or light text on a background color
 * @param backgroundColor - Color in hex format (e.g., #FFFF00)
 * @returns 'dark' for dark text on light backgrounds, 'light' for light text on dark backgrounds
 */
export function getTextColorForBackground(backgroundColor: string): 'dark' | 'light' {
  const luminance = getColorLuminance(backgroundColor);
  // Threshold of 0.5 provides good contrast for most colors
  return luminance > 0.5 ? 'dark' : 'light';
}

/**
 * Gets the actual text color (hex) based on background brightness
 * @param backgroundColor - Color in hex format
 * @returns Dark color (#333333) for light backgrounds, white (#FFFFFF) for dark backgrounds
 */
export function getTextColor(backgroundColor: string): string {
  const textType = getTextColorForBackground(backgroundColor);
  return textType === 'dark' ? '#333333' : '#FFFFFF';
}
